import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search, RefreshCw, X, Send, Lock, Building2, Paperclip, Megaphone, Flag
} from "lucide-react";
import {
  changeSupportStatus,
  getSupportTicket,
  listSupportAttachments,
  listSupportTickets,
  postSupportMessage,
  updateSupportTicket
} from "../services/api";
import type {
  TicketAttachment, TicketDetail, TicketMessage, TicketPriority, TicketStatus, TicketSummary
} from "../types";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

/* ----------------------------------------------------------------------------
 * Status + priority display metadata. The board has one column per status, in
 * pipeline order. Priority drives the small chip colour on each card.
 * --------------------------------------------------------------------------*/

const STATUS_ORDER: TicketStatus[] = ["backlog", "in_progress", "awaiting_client_review", "done"];

const STATUS_LABELS: Record<TicketStatus, string> = {
  backlog: "Backlog",
  in_progress: "In Progress",
  awaiting_client_review: "Awaiting Client Review",
  done: "Done"
};

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent"
};

/**
 * Where a ticket came from. 'orbit' (the web app, raised by a real tenant) is
 * the default and shows the company name as it always has. Anything else is
 * one of our own apps reporting a bug or an idea, with no customer behind it,
 * and gets a badge so the two are never confused on the board.
 */
const SOURCE_LABELS: Record<string, string> = {
  "sales-coach": "Sales Coach"
};

/** Maps a priority to one of the shared `.pill` colour modifiers. */
function priorityPillClass(p: TicketPriority): string {
  switch (p) {
    case "urgent": return "danger";
    case "high": return "warn";
    case "normal": return "info";
    case "low": return "";
    default: return "";
  }
}

const PRIORITIES: TicketPriority[] = ["low", "normal", "high", "urgent"];

/* ----------------------------------------------------------------------------
 * Page
 * --------------------------------------------------------------------------*/

export function SupportPage() {
  usePageTitle("Support");
  const toast = useToast();

  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters. `q` is debounced into `appliedQ` so we don't refetch on every key.
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [company, setCompany] = useState("");
  // Read/unread is a SERVER filter, not a client one: an unanswered reply on a
  // closed ticket sits in Done and is the exact thing this is for, so it has to
  // reach rows the board would otherwise scroll past.
  const [readFilter, setReadFilter] = useState<"all" | "unread" | "read">("all");

  const [openId, setOpenId] = useState<string | null>(null);

  // Drag-and-drop state: the card currently being dragged, plus a pending
  // "Done" move that needs a resolution captured before it commits.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [pendingDone, setPendingDone] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSupportTickets({
        q: appliedQ.trim() || undefined,
        businessId: company.trim() || undefined,
        unread: readFilter === "all" ? undefined : readFilter === "unread"
      });
      setTickets(res.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load tickets.");
    } finally {
      setLoading(false);
    }
  }, [appliedQ, company, readFilter]);

  useEffect(() => { void load(); }, [load]);

  // Debounce the search box -> appliedQ (drives the server `q` param).
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedQ(q), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  // The company filter is a free-text match against the (server-filtered)
  // result set's companyName — handy when the operator doesn't know the id.
  const visible = useMemo(() => {
    const c = company.trim().toLowerCase();
    if (!c) return tickets;
    return tickets.filter((t) => t.companyName.toLowerCase().includes(c) || t.businessId.toLowerCase().includes(c));
  }, [tickets, company]);

  // Counts what is loaded, so on the "Read" filter it correctly reads zero.
  const unreadCount = useMemo(() => visible.filter((t) => t.operatorUnread).length, [visible]);

  const byStatus = useMemo(() => {
    const groups: Record<TicketStatus, TicketSummary[]> = {
      backlog: [], in_progress: [], awaiting_client_review: [], done: []
    };
    for (const t of visible) groups[t.status].push(t);
    return groups;
  }, [visible]);

  // When the detail panel saves a change, fold the updated row back into the
  // board so the card reflects the new status/priority without a full refetch.
  const onTicketChanged = useCallback((t: TicketDetail) => {
    setTickets((prev) => {
      const next = prev.map((row) => (row.id === t.id ? { ...row, ...stripDetail(t) } : row));
      return next.some((row) => row.id === t.id) ? next : [stripDetail(t), ...prev];
    });
  }, []);

  // Opening a ticket clears its operator-unread dot on the board immediately.
  const onOpen = useCallback((id: string) => {
    setOpenId(id);
    setTickets((prev) => prev.map((row) => (row.id === id ? { ...row, operatorUnread: false } : row)));
  }, []);

  // Move a ticket to another column (drag-drop). Optimistic: the card jumps
  // immediately, then we confirm with the server and revert on failure.
  // Dropping into "Done" needs a resolution, so collect it in a modal first.
  const moveTicket = useCallback(async (id: string, toStatus: TicketStatus) => {
    const current = tickets.find((t) => t.id === id);
    if (!current || current.status === toStatus) return;
    if (toStatus === "done") { setPendingDone(id); return; }
    const prevStatus = current.status;
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: toStatus } : t)));
    try {
      const res = await changeSupportStatus(id, toStatus);
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...stripDetail(res.ticket) } : t)));
      toast.push("success", `Moved to ${STATUS_LABELS[toStatus]}.`);
    } catch (err) {
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: prevStatus } : t)));
      toast.push("error", err instanceof Error ? err.message : "Could not move the ticket.");
    }
  }, [tickets, toast]);

  // Confirm the resolution for a ticket dragged into "Done".
  const onResolveDone = useCallback(async (resolution: string) => {
    if (!pendingDone) return;
    const id = pendingDone;
    const prevStatus = tickets.find((t) => t.id === id)?.status;
    setResolving(true);
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: "done" } : t)));
    try {
      const res = await changeSupportStatus(id, "done", resolution);
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...stripDetail(res.ticket) } : t)));
      setPendingDone(null);
      toast.push("success", "Ticket resolved.");
    } catch (err) {
      if (prevStatus) setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: prevStatus } : t)));
      toast.push("error", err instanceof Error ? err.message : "Could not resolve the ticket.");
    } finally {
      setResolving(false);
    }
  }, [pendingDone, tickets, toast]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Support</h1>
          <p className="muted">Tickets raised by customers, grouped by where they are in the pipeline. Drag a card between columns to move it.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : undefined} /> Refresh
          </button>
        </div>
      </header>

      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px", maxWidth: "none" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search tickets by ref, subject, customer…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="search-bar" style={{ flex: "0 1 280px", maxWidth: "none" }}>
            <Building2 size={14} />
            <input
              type="search"
              placeholder="Filter by company…"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="support-readfilter" role="group" aria-label="Read state">
            {([
              ["all", "All"],
              ["unread", unreadCount > 0 ? `Unread (${unreadCount})` : "Unread"],
              ["read", "Read"]
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={"btn btn-ghost" + (readFilter === value ? " is-active" : "")}
                onClick={() => setReadFilter(value)}
              >
                {value === "unread" ? <span className="support-unread" aria-hidden /> : null}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? <div className="card" style={{ borderColor: "rgba(255,92,122,0.3)" }}>
        <p style={{ color: "var(--danger)", margin: 0 }}>{error}</p>
      </div> : null}

      <div className="support-board">
        {STATUS_ORDER.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            tickets={byStatus[status]}
            loading={loading}
            onOpen={onOpen}
            draggingId={draggingId}
            onDragStart={setDraggingId}
            onDragEnd={() => setDraggingId(null)}
            onDropTicket={moveTicket}
          />
        ))}
      </div>

      {openId ? (
        <TicketDetailPanel
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={onTicketChanged}
          onError={(m) => toast.push("error", m)}
        />
      ) : null}

      {pendingDone ? (
        <ResolveModal
          busy={resolving}
          onCancel={() => setPendingDone(null)}
          onConfirm={onResolveDone}
        />
      ) : null}
    </div>
  );
}

/** Narrows a TicketDetail back to the summary fields the board cards use. */
function stripDetail(t: TicketDetail): TicketSummary {
  const { body: _body, resolution: _resolution, ...summary } = t;
  return summary;
}

/* ----------------------------------------------------------------------------
 * Board column + cards
 * --------------------------------------------------------------------------*/

function BoardColumn({
  status, tickets, loading, onOpen, draggingId, onDragStart, onDragEnd, onDropTicket
}: {
  status: TicketStatus;
  tickets: TicketSummary[];
  loading: boolean;
  onOpen: (id: string) => void;
  draggingId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDropTicket: (id: string, status: TicketStatus) => void;
}) {
  const [over, setOver] = useState(false);
  const canDrop = draggingId != null;
  return (
    <section
      className={"support-col" + (over && canDrop ? " is-drop-target" : "")}
      data-status={status}
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (draggingId) onDropTicket(draggingId, status); }}
    >
      <div className="support-col-head">
        <span className={`support-col-dot ${status}`} aria-hidden />
        <h3 style={{ margin: 0 }}>{STATUS_LABELS[status]}</h3>
        <span className="support-col-count">{tickets.length}</span>
      </div>
      <div className="support-col-body">
        {loading && tickets.length === 0 ? (
          <p className="muted" style={{ padding: "8px 4px", fontSize: 12.5 }}>Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="muted" style={{ padding: "8px 4px", fontSize: 12.5 }}>No tickets.</p>
        ) : (
          tickets.map((t) => (
            <TicketCard
              key={t.id}
              ticket={t}
              onOpen={onOpen}
              dragging={draggingId === t.id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))
        )}
      </div>
    </section>
  );
}

function TicketCard({
  ticket, onOpen, dragging, onDragStart, onDragEnd
}: {
  ticket: TicketSummary;
  onOpen: (id: string) => void;
  dragging: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
}) {
  return (
    <button
      type="button"
      className={
        "card card-hover support-card"
        + (dragging ? " is-dragging" : "")
        + (ticket.operatorUnread ? " is-unread" : "")
      }
      draggable
      onClick={() => onOpen(ticket.id)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", ticket.id);
        onDragStart(ticket.id);
      }}
      onDragEnd={onDragEnd}
    >
      <div className="row between" style={{ gap: 8 }}>
        <span className="mono support-ref">{ticket.ref}</span>
        <div className="row" style={{ gap: 6 }}>
          {ticket.operatorUnread ? <span className="support-unread" title="Unread customer reply" /> : null}
          <span className={`pill ${priorityPillClass(ticket.priority)}`}>{PRIORITY_LABELS[ticket.priority]}</span>
        </div>
      </div>
      <div className="support-card-subject">{ticket.subject}</div>
      <div className="row between" style={{ gap: 8, marginTop: 4 }}>
        {/* A ticket raised by one of our own apps has no tenant behind it, so
            it gets a badge instead of a building icon. Without this it reads
            as a customer complaint, which it isn't. */}
        {ticket.source && ticket.source !== "orbit" ? (
          <span className="support-card-company support-source" title={`Raised from ${SOURCE_LABELS[ticket.source] ?? ticket.source}`}>
            <Megaphone size={12} /> {SOURCE_LABELS[ticket.source] ?? ticket.source}
          </span>
        ) : (
          <span className="support-card-company">
            <Building2 size={12} /> {ticket.companyName}
            {/* Raised by a REP from the phone app rather than by the owner.
                Worth calling out: replying here reaches the rep in the field,
                and a rep issue usually means the product is broken for them. */}
            {ticket.repIssueId ? (
              <span
                className="support-source"
                style={{ marginLeft: 6 }}
                title={`Reported from the Orbit app by ${ticket.repName || "a rep"}`}
              >
                <Flag size={11} /> {ticket.repName || "Rep"}
              </span>
            ) : null}
          </span>
        )}
        <span className="faint" style={{ fontSize: 11 }}>{shortDate(ticket.createdAt)}</span>
      </div>
    </button>
  );
}

/* ----------------------------------------------------------------------------
 * Ticket detail — right-hand slide-over panel
 * --------------------------------------------------------------------------*/

function TicketDetailPanel({
  id, onClose, onChanged, onError
}: {
  id: string;
  onClose: () => void;
  onChanged: (t: TicketDetail) => void;
  onError: (message: string) => void;
}) {
  const toast = useToast();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [company, setCompany] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Inline control state
  const [savingField, setSavingField] = useState<null | "priority" | "status" | "operator">(null);
  const [assignee, setAssignee] = useState("");

  // Reply composer
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  // "Done" resolution modal
  const [resolveOpen, setResolveOpen] = useState(false);

  const threadRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [res, attRes] = await Promise.all([
        getSupportTicket(id),
        listSupportAttachments(id).catch(() => ({ attachments: [] }))
      ]);
      setTicket(res.ticket);
      setMessages(res.messages ?? []);
      setAttachments(attRes.attachments ?? []);
      setCompany(res.company);
      setAssignee(res.ticket.assignedOperator ?? "");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load this ticket.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  // Escape closes the panel; lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !resolveOpen) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, resolveOpen]);

  // Keep the thread scrolled to the latest message as it grows.
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages.length, loading]);

  const applyTicket = useCallback((next: TicketDetail) => {
    setTicket(next);
    setAssignee(next.assignedOperator ?? "");
    onChanged(next);
  }, [onChanged]);

  const onChangePriority = async (priority: TicketPriority) => {
    if (!ticket || priority === ticket.priority) return;
    setSavingField("priority");
    try {
      const res = await updateSupportTicket(ticket.id, { priority });
      applyTicket(res.ticket);
      toast.push("success", `Priority set to ${PRIORITY_LABELS[priority]}.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not change priority.");
    } finally {
      setSavingField(null);
    }
  };

  const onChangeStatus = async (status: TicketStatus) => {
    if (!ticket || status === ticket.status) return;
    // Moving to "done" requires a resolution — collect it in a modal first.
    if (status === "done") {
      setResolveOpen(true);
      return;
    }
    setSavingField("status");
    try {
      const res = await changeSupportStatus(ticket.id, status);
      applyTicket(res.ticket);
      toast.push("success", `Moved to ${STATUS_LABELS[status]}.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not change status.");
    } finally {
      setSavingField(null);
    }
  };

  const onResolve = async (resolution: string) => {
    if (!ticket) return;
    setSavingField("status");
    try {
      const res = await changeSupportStatus(ticket.id, "done", resolution);
      applyTicket(res.ticket);
      setResolveOpen(false);
      toast.push("success", "Ticket resolved.");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not resolve the ticket.");
    } finally {
      setSavingField(null);
    }
  };

  const onSaveAssignee = async () => {
    if (!ticket) return;
    const next = assignee.trim();
    if (next === (ticket.assignedOperator ?? "")) return;
    setSavingField("operator");
    try {
      const res = await updateSupportTicket(ticket.id, { assignedOperator: next || null });
      applyTicket(res.ticket);
      toast.push("success", next ? `Assigned to ${next}.` : "Unassigned.");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update the assignee.");
    } finally {
      setSavingField(null);
    }
  };

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;
    const text = reply.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await postSupportMessage(ticket.id, text, internal);
      setMessages((prev) => [...prev, res.message]);
      setReply("");
      // A public reply hands the ticket to the customer, so the server puts it
      // in Client review. Fold that back in or the panel and the board keep
      // showing the old column until the next refresh.
      if (res.status && res.status !== ticket.status) {
        const moved = { ...ticket, status: res.status };
        setTicket(moved);
        onChanged(moved);
        toast.push("success", `Reply sent. Moved to ${STATUS_LABELS[res.status]}.`);
      } else {
        toast.push("success", internal ? "Internal note added." : "Reply sent.");
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not send the message.");
    } finally {
      setSending(false);
    }
  };

  const companyName = ticket?.companyName ?? company?.name ?? "—";

  const ticketTs = ticket ? new Date(ticket.createdAt).getTime() : 0;
  const initialAttachments = attachments.filter(
    (a) => a.messageId === null && new Date(a.createdAt).getTime() <= ticketTs + 300_000
  );
  const uploadEvents = attachments.filter(
    (a) => a.messageId === null && new Date(a.createdAt).getTime() > ticketTs + 300_000
  );
  const timeline: Array<{ kind: "msg"; m: TicketMessage } | { kind: "upload"; a: TicketAttachment }> = [
    ...messages.map((m) => ({ kind: "msg" as const, m })),
    ...uploadEvents.map((a) => ({ kind: "upload" as const, a })),
  ].sort(
    (x, y) =>
      new Date(x.kind === "msg" ? x.m.createdAt : x.a.createdAt).getTime() -
      new Date(y.kind === "msg" ? y.m.createdAt : y.a.createdAt).getTime()
  );

  return (
    <>
      <div className="support-panel-backdrop" onClick={onClose} />
      <aside className="support-panel" role="dialog" aria-modal aria-label="Ticket detail">
        <div className="support-panel-head">
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <span className="mono support-ref" style={{ fontSize: 14 }}>{ticket?.ref ?? "…"}</span>
              {ticket ? (
                <span className={`pill ${statusPillClass(ticket.status)}`}>{STATUS_LABELS[ticket.status]}</span>
              ) : null}
            </div>
            <h2 style={{ margin: "8px 0 4px", fontSize: 17 }}>{ticket?.subject ?? (loading ? "Loading…" : "Ticket")}</h2>
            <div className="row wrap" style={{ gap: 8, fontSize: 12.5, color: "var(--text-mute)" }}>
              <span className="row" style={{ gap: 4 }}><Building2 size={12} /> {companyName}</span>
              <span>·</span>
              <span>{ticket?.createdByEmail ?? "—"}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {loadError ? (
          <div style={{ padding: 22 }}>
            <p style={{ color: "var(--danger)" }}>{loadError}</p>
            <button className="btn btn-ghost btn-sm" onClick={() => void load()}>
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        ) : !ticket ? (
          <div style={{ padding: 22 }} className="muted">Loading ticket…</div>
        ) : (
          <>
            {/* ---- Controls ---- */}
            <div className="support-controls">
              <label>
                Status
                <select
                  value={ticket.status}
                  disabled={savingField === "status"}
                  onChange={(e) => void onChangeStatus(e.target.value as TicketStatus)}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </label>
              <label>
                Priority
                <select
                  value={ticket.priority}
                  disabled={savingField === "priority"}
                  onChange={(e) => void onChangePriority(e.target.value as TicketPriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                  ))}
                </select>
              </label>
              <label>
                Assigned operator
                <div className="row" style={{ gap: 6 }}>
                  <input
                    value={assignee}
                    placeholder="operator@orbitsa.co.za"
                    disabled={savingField === "operator"}
                    onChange={(e) => setAssignee(e.target.value)}
                    onBlur={() => void onSaveAssignee()}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onSaveAssignee(); } }}
                    style={{ flex: 1 }}
                  />
                </div>
              </label>
            </div>

            {ticket.resolution ? (
              <div className="support-resolution">
                <div className="support-resolution-label">Resolution</div>
                <div>{ticket.resolution}</div>
              </div>
            ) : null}

            {/* ---- Thread ---- */}
            <div className="support-thread" ref={threadRef}>
              {/* The original request lives on the ticket itself, not in messages. */}
              <ThreadBubble
                authorType="customer"
                authorEmail={ticket.createdByEmail}
                authorName={ticket.repName}
                body={ticket.body}
                internal={false}
                createdAt={ticket.createdAt}
                attachments={initialAttachments}
                original
              />
              {timeline.map((entry) =>
                entry.kind === "msg" ? (
                  <ThreadBubble
                    key={entry.m.id}
                    authorType={entry.m.authorType}
                    authorEmail={entry.m.authorEmail}
                    authorName={entry.m.authorName}
                    body={entry.m.body}
                    internal={entry.m.internal}
                    createdAt={entry.m.createdAt}
                    attachments={attachments.filter((a) => a.messageId === entry.m.id)}
                  />
                ) : (
                  <div key={entry.a.id} className="support-upload-event">
                    <span className="faint" style={{ fontSize: 11 }}>Customer uploaded</span>
                    <AttachmentChip attachment={entry.a} />
                    <span className="faint" style={{ fontSize: 11 }}>{fullDate(entry.a.createdAt)}</span>
                  </div>
                )
              )}
            </div>

            {/* ---- Composer ---- */}
            <form className="support-composer" onSubmit={onSend}>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={internal ? "Write an internal note (not visible to the customer)…" : "Write a reply to the customer…"}
                rows={3}
                style={internal ? { borderColor: "var(--warn)", background: "var(--warn-soft)" } : undefined}
              />
              <div className="row between" style={{ gap: 10 }}>
                <label className="support-internal-toggle">
                  <input
                    type="checkbox"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                  />
                  <Lock size={13} /> Internal note
                </label>
                <button className="btn btn-primary btn-sm" type="submit" disabled={sending || !reply.trim()}>
                  {internal ? <Lock size={14} /> : <Send size={14} />}
                  {sending ? "Sending…" : internal ? "Add note" : "Send reply"}
                </button>
              </div>
            </form>
          </>
        )}
      </aside>

      {resolveOpen ? (
        <ResolveModal
          busy={savingField === "status"}
          onCancel={() => setResolveOpen(false)}
          onConfirm={onResolve}
        />
      ) : null}
    </>
  );
}

/* A single message bubble. The very first one (the original request) is tagged
 * `original`; operator-only internal notes get a distinct, unmistakable style. */
function ThreadBubble({
  authorType, authorEmail, authorName, body, internal, createdAt, attachments, original
}: {
  authorType: TicketMessage["authorType"];
  authorEmail: string;
  /** Set on replies mirrored up from a rep's thread — show the person, not the address. */
  authorName?: string | null;
  body: string;
  internal: boolean;
  createdAt: string;
  attachments?: TicketAttachment[];
  original?: boolean;
}) {
  const side = authorType === "operator" ? "operator" : "customer";
  const cls = internal ? "internal" : side;
  return (
    <div className={`support-bubble support-bubble-${cls}`}>
      <div className="support-bubble-meta">
        {internal ? (
          <span className="support-internal-badge"><Lock size={11} /> Internal note</span>
        ) : (
          <span className="support-bubble-author">
            {authorName ? `${authorName} (${authorEmail})` : authorEmail}
            {original ? <span className="support-bubble-original"> · original request</span> : null}
          </span>
        )}
        <span className="faint" style={{ fontSize: 11 }}>{fullDate(createdAt)}</span>
      </div>
      <div className="support-bubble-body">{body}</div>
      {attachments && attachments.length > 0 ? (
        <div className="support-attachments">
          {attachments.map((a) => (
            <AttachmentChip key={a.id} attachment={a} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AttachmentChip({ attachment }: { attachment: TicketAttachment }) {
  const kb = Math.round(attachment.sizeBytes / 1024);
  const label = `${attachment.fileName} (${kb < 1 ? "<1" : kb} KB)`;
  if (attachment.url) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="support-attachment-chip"
        title={label}
      >
        <Paperclip size={12} /> {attachment.fileName}
      </a>
    );
  }
  return (
    <span className="support-attachment-chip support-attachment-chip-nourl" title={`${label} — S3 bucket not configured`}>
      <Paperclip size={12} /> {attachment.fileName}
    </span>
  );
}

/* The "What was the solution?" modal shown when moving a ticket to Done.
 * A resolution is required — the backend 400s on an empty one. Uses a textarea
 * (the shared usePrompt dialog is single-line only), but otherwise mirrors the
 * app's modal markup/classes. */
function ResolveModal({
  busy, onCancel, onConfirm
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (resolution: string) => void;
}) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const blocked = value.trim().length === 0 || busy;

  useEffect(() => {
    const t = window.setTimeout(() => ref.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => { window.clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [onCancel]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
        <h2>Resolve ticket</h2>
        <p>What was the solution? This is saved on the ticket and shown to the customer.</p>
        <form
          className="form"
          onSubmit={(e) => { e.preventDefault(); if (!blocked) onConfirm(value.trim()); }}
        >
          <label>
            Resolution
            <textarea
              ref={ref}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. Re-provisioned the customer's Twilio number and confirmed inbound calls connect."
              rows={4}
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={blocked}>
              {busy ? "Resolving…" : "Mark as done"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------*/

function statusPillClass(s: TicketStatus): string {
  switch (s) {
    case "done": return "success";
    case "in_progress": return "info";
    case "awaiting_client_review": return "warn";
    case "backlog": return "";
    default: return "";
  }
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
}

function fullDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-ZA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
